import React from "react";
import { useStore } from "@/stores/useStore";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Base/Select";
import { Button } from "@/components/Base/Button";
import {
  Card as UICard,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/Base/Card";
import {
  SettingsIcon,
  BarChart3Icon,
  ScatterChartIcon,
  Users2Icon,
} from "lucide-react";
import { ConfigurationSelector } from "./ConfigurationSelector";
import { ContainerTimeConfig } from "@/types/v2/Metrics";

type TimeEvent = "enters" | "exits";
type ColorCoding = "none" | "technician" | "serviceAdvisor";

interface MetricsConfigurationPanelProps {
  firstContainer: string;
  setFirstContainer: (value: string) => void;
  firstEvent: TimeEvent;
  setFirstEvent: (value: TimeEvent) => void;
  secondContainer: string;
  setSecondContainer: (value: string) => void;
  secondEvent: TimeEvent;
  setSecondEvent: (value: TimeEvent) => void;
  timeUnit: "minutes" | "hours" | "days";
  setTimeUnit: (value: "minutes" | "hours" | "days") => void;
  chartType: "bar" | "scatter";
  setChartType: (value: "bar" | "scatter") => void;
  colorCoding: ColorCoding;
  setColorCoding: (value: ColorCoding) => void;
  showTable: boolean;
  setShowTable: (value: boolean) => void;
  currentConfig: Partial<ContainerTimeConfig>;
  onConfigLoad: (config: ContainerTimeConfig) => void;
}

export const MetricsConfigurationPanel: React.FC<
  MetricsConfigurationPanelProps
> = ({
  firstContainer,
  setFirstContainer,
  firstEvent,
  setFirstEvent,
  secondContainer,
  setSecondContainer,
  secondEvent,
  setSecondEvent,
  timeUnit,
  setTimeUnit,
  chartType,
  setChartType,
  colorCoding,
  setColorCoding,
  showTable,
  setShowTable,
  currentConfig,
  onConfigLoad,
}) => {
  const { shop } = useStore();
  const containers = shop?.containers ?? [];

  const isValidConfiguration = firstContainer && secondContainer;

  return (
    <UICard className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <SettingsIcon className="h-5 w-5" />
          Metrics Configuration
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Configuration Selector */}
        <ConfigurationSelector
          currentConfig={currentConfig}
          onConfigLoad={onConfigLoad}
        />

        {/* Container and Event Selection */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">First Container</label>
            <Select value={firstContainer} onValueChange={setFirstContainer}>
              <SelectTrigger>
                <SelectValue placeholder="Select container" />
              </SelectTrigger>
              <SelectContent>
                {containers.map((container) => (
                  <SelectItem key={container.id} value={container.id}>
                    {container.displayName ?? container.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">First Event</label>
            <Select value={firstEvent} onValueChange={setFirstEvent}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="enters">Enters</SelectItem>
                <SelectItem value="exits">Exits</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Second Container</label>
            <Select value={secondContainer} onValueChange={setSecondContainer}>
              <SelectTrigger>
                <SelectValue placeholder="Select container" />
              </SelectTrigger>
              <SelectContent>
                {containers.map((container) => (
                  <SelectItem key={container.id} value={container.id}>
                    {container.displayName ?? container.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Second Event</label>
            <Select value={secondEvent} onValueChange={setSecondEvent}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="enters">Enters</SelectItem>
                <SelectItem value="exits">Exits</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Display Options */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="space-y-2">
            <label className="text-sm font-medium">Time Unit</label>
            <Select value={timeUnit} onValueChange={setTimeUnit}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="minutes">Minutes</SelectItem>
                <SelectItem value="hours">Hours</SelectItem>
                <SelectItem value="days">Days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Chart Type</label>
            <Select value={chartType} onValueChange={setChartType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="scatter">
                  <div className="flex items-center gap-2">
                    <ScatterChartIcon className="h-4 w-4" />
                    Scatter Plot
                  </div>
                </SelectItem>
                <SelectItem value="bar">
                  <div className="flex items-center gap-2">
                    <BarChart3Icon className="h-4 w-4" />
                    Bar Chart
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Color Coding</label>
            <Select value={colorCoding} onValueChange={setColorCoding}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="serviceAdvisor">
                  <div className="flex items-center gap-2">
                    <Users2Icon className="h-4 w-4" />
                    Service Advisor
                  </div>
                </SelectItem>
                <SelectItem value="technician">
                  <div className="flex items-center gap-2">
                    <Users2Icon className="h-4 w-4" />
                    Technician
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between">
          <Button
            onClick={() => setShowTable(!showTable)}
            variant="outline"
            disabled={!isValidConfiguration}
          >
            {showTable ? "Hide Table" : "Show Table"}
          </Button>

          <div className="text-sm text-muted-foreground">
            {isValidConfiguration
              ? "✓ Configuration ready"
              : "Select containers to view metrics"}
          </div>
        </div>
      </CardContent>
    </UICard>
  );
};
